import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import type { EventCategory, Severity, Vendor } from '@ixam/shared';
import {
  createRule,
  deleteRule,
  listRules,
  updateRule,
  type CreateRuleInput,
  type RuleDto,
} from '../api/rules.api';
import { listChannels } from '../api/channels.api';
import { Spinner } from '../components/Spinner';
import { Modal } from './Servers';
import { ConfirmDialog, useConfirm } from '../components/ConfirmDialog';

const SEVERITIES: Severity[] = ['critical', 'warning', 'info', 'ok'];
const VENDORS: Vendor[] = ['dell', 'hp', 'lenovo'];
const CATEGORIES: EventCategory[] = [
  'storage',
  'thermal',
  'power',
  'network',
  'system',
  'security',
  'other',
];

const SEVERITY_LABEL_IT: Record<Severity, string> = {
  critical: 'critico',
  warning: 'attenzione',
  info: 'info',
  ok: 'ok',
};
const CATEGORY_LABEL_IT: Record<EventCategory, string> = {
  storage: 'storage',
  thermal: 'termico',
  power: 'alimentazione',
  network: 'rete',
  system: 'sistema',
  security: 'sicurezza',
  other: 'altro',
};

export function Rules() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const confirm = useConfirm();

  const { data: rules, isLoading } = useQuery({
    queryKey: ['rules'],
    queryFn: listRules,
  });

  const { data: channels } = useQuery({
    queryKey: ['channels'],
    queryFn: listChannels,
  });

  const create = useMutation({
    mutationFn: createRule,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rules'] });
      setShowAdd(false);
    },
  });

  const del = useMutation({
    mutationFn: deleteRule,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
  });

  const reprio = useMutation({
    mutationFn: ({ id, priority }: { id: string; priority: number }) =>
      updateRule(id, { priority }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
  });

  const channelNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of channels ?? []) map.set(c._id, c.name);
    return map;
  }, [channels]);

  return (
    <div className="p-5 md:p-10 max-w-7xl mx-auto space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-bold text-3xl text-ink">Regole di instradamento</h1>
          <p className="text-sm text-ink-muted mt-1">
            Decidi quali eventi vengono notificati e su quali canali.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="ixam-btn"
          disabled={!channels || channels.length === 0}
          title={
            channels?.length === 0 ? 'Aggiungi prima un canale dalla pagina Canali' : ''
          }
        >
          <Plus className="w-4 h-4" />
          Aggiungi regola
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-3 text-ink-muted">
          <Spinner size={20} /> Caricamento…
        </div>
      ) : (
        <div className="ixam-card overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-paper-soft text-xs uppercase tracking-wide text-ink-faint">
              <tr>
                <th className="text-left px-5 py-3 font-medium w-28">Priorità</th>
                <th className="text-left px-5 py-3 font-medium">Nome</th>
                <th className="text-left px-5 py-3 font-medium">Condizioni</th>
                <th className="text-left px-5 py-3 font-medium">Canali</th>
                <th className="text-left px-5 py-3 font-medium w-32">Dedup (min)</th>
                <th className="text-left px-5 py-3 font-medium w-20">Stop?</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-soft">
              {rules?.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-ink-muted">
                    Nessuna regola. Aggiungi un canale Telegram e una regola
                    verrà creata automaticamente per gli eventi critici.
                  </td>
                </tr>
              )}
              {rules?.map((r, i) => (
                <tr key={r._id} className="hover:bg-paper-soft/40 align-top">
                  <td className="px-5 py-3.5 font-mono text-sm">
                    <div className="flex items-center gap-1.5">
                      <span className="text-ink">{r.priority}</span>
                      <div className="flex flex-col">
                        <button
                          title="Sposta su (priorità più bassa = prima)"
                          onClick={() =>
                            reprio.mutate({ id: r._id, priority: r.priority - 1 })
                          }
                          disabled={i === 0}
                          className="text-ink-faint hover:text-ink disabled:opacity-20"
                        >
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button
                          title="Sposta giù"
                          onClick={() =>
                            reprio.mutate({ id: r._id, priority: r.priority + 1 })
                          }
                          disabled={i === rules.length - 1}
                          className="text-ink-faint hover:text-ink disabled:opacity-20"
                        >
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 font-medium text-ink">
                    {r.name}
                    {!r.enabled && (
                      <span className="ml-2 text-xs text-ink-faint">(disattivata)</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-xs text-ink-muted">
                    <ConditionsSummary rule={r} />
                  </td>
                  <td className="px-5 py-3.5 text-xs text-ink-muted">
                    {r.actions.channelIds.length === 0 ? (
                      <span className="text-ink-faint italic">nessuno</span>
                    ) : (
                      r.actions.channelIds
                        .map((id) => channelNameById.get(id) ?? id.slice(0, 8) + '…')
                        .join(', ')
                    )}
                  </td>
                  <td className="px-5 py-3.5 font-mono text-xs">
                    {r.actions.suppressDuplicatesMinutes}
                  </td>
                  <td className="px-5 py-3.5 text-xs">
                    {r.stopProcessing ? (
                      <span className="text-sev-warning">stop</span>
                    ) : (
                      <span className="text-ink-faint">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <button
                      title="Elimina regola"
                      onClick={() =>
                        confirm.ask({
                          title: `Eliminare la regola "${r.name}"?`,
                          body: 'Gli eventi futuri che combaciavano con questa regola non saranno più instradati ai canali configurati.',
                          confirmLabel: 'Elimina',
                          destructive: true,
                          onConfirm: () => del.mutate(r._id),
                        })
                      }
                      className="ixam-icon-btn text-sev-critical"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && channels && (
        <AddRuleModal
          channels={channels.map((c) => ({ id: c._id, name: c.name, type: c.type }))}
          onClose={() => setShowAdd(false)}
          onSave={(r) => create.mutate(r)}
          loading={create.isPending}
          error={create.error}
        />
      )}

      <ConfirmDialog request={confirm.request} onClose={confirm.dismiss} />
    </div>
  );
}

function ConditionsSummary({ rule }: { rule: RuleDto }) {
  const c = rule.conditions;
  const bits: string[] = [];
  if (c.severities.length)
    bits.push(`severità ∈ {${c.severities.map((s) => SEVERITY_LABEL_IT[s]).join(', ')}}`);
  if (c.vendors.length) bits.push(`vendor ∈ {${c.vendors.join(', ')}}`);
  if (c.categories.length)
    bits.push(`categoria ∈ {${c.categories.map((x) => CATEGORY_LABEL_IT[x]).join(', ')}}`);
  if (c.serverIds.length) bits.push(`server ∈ {${c.serverIds.length} id}`);
  if (c.tagsAny.length) bits.push(`tag ∈ {${c.tagsAny.join(', ')}}`);
  if (bits.length === 0) return <span className="text-ink-faint italic">qualunque evento</span>;
  return <span>{bits.join(' E ')}</span>;
}

function AddRuleModal({
  channels,
  onClose,
  onSave,
  loading,
  error,
}: {
  channels: { id: string; name: string; type: string }[];
  onClose: () => void;
  onSave: (r: CreateRuleInput) => void;
  loading: boolean;
  error: unknown;
}) {
  const [name, setName] = useState('');
  const [priority, setPriority] = useState(100);
  const [enabled, setEnabled] = useState(true);
  const [stopProcessing, setStopProcessing] = useState(false);
  const [severities, setSeverities] = useState<Severity[]>(['critical']);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [suppressMinutes, setSuppressMinutes] = useState(30);

  function toggle<T>(arr: T[], v: T): T[] {
    return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    onSave({
      name,
      priority,
      enabled,
      stopProcessing,
      conditions: { severities, vendors, categories },
      actions: { channelIds, suppressDuplicatesMinutes: suppressMinutes },
    });
  }

  const errMsg = error
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((error as any)?.response?.data?.error?.message ?? 'Salvataggio non riuscito')
    : null;

  return (
    <Modal title="Nuova regola di instradamento" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Nome" className="col-span-2">
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="critici → telegram"
              className="ixam-input"
            />
          </Field>
          <Field label="Priorità (più bassa = prima)">
            <input
              type="number"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
              className="ixam-input"
            />
          </Field>
        </div>

        <div>
          <div className="text-xs font-medium text-ink mb-2">
            Condizioni — l'evento deve soddisfare TUTTI i campi indicati
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-paper-soft rounded-lg">
            <ChipGroup
              label="Severità"
              options={SEVERITIES}
              value={severities}
              labels={SEVERITY_LABEL_IT}
              onToggle={(v) => setSeverities(toggle(severities, v))}
            />
            <ChipGroup
              label="Vendor"
              options={VENDORS}
              value={vendors}
              onToggle={(v) => setVendors(toggle(vendors, v))}
            />
            <ChipGroup
              label="Categoria"
              options={CATEGORIES}
              value={categories}
              labels={CATEGORY_LABEL_IT}
              onToggle={(v) => setCategories(toggle(categories, v))}
            />
          </div>
          <div className="text-xs text-ink-faint mt-2">
            Lascia un gruppo vuoto per non filtrare quel campo.
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-ink mb-2">Canali da notificare *</div>
          <div className="space-y-1 p-3 bg-paper-soft rounded-lg max-h-44 overflow-y-auto">
            {channels.length === 0 && (
              <div className="text-sm text-ink-faint">Nessun canale disponibile</div>
            )}
            {channels.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:text-ink">
                <input
                  type="checkbox"
                  checked={channelIds.includes(c.id)}
                  onChange={() => setChannelIds(toggle(channelIds, c.id))}
                  className="accent-accent"
                />
                <span className="text-ink">
                  {c.name}{' '}
                  <span className="text-xs text-ink-faint">({c.type})</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Finestra dedup (minuti)">
            <input
              type="number"
              min={0}
              value={suppressMinutes}
              onChange={(e) => setSuppressMinutes(Number(e.target.value))}
              className="ixam-input"
            />
          </Field>
          <label className="flex items-center gap-2 mt-7 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="accent-accent"
            />
            Attivata
          </label>
          <label className="flex items-center gap-2 mt-7 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={stopProcessing}
              onChange={(e) => setStopProcessing(e.target.checked)}
              className="accent-accent"
            />
            Stop dopo match
          </label>
        </div>

        {errMsg && (
          <div className="px-3 py-2 bg-sev-critical-soft border border-sev-critical/30 rounded-lg text-xs text-sev-critical">
            {errMsg}
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="ixam-btn-ghost flex-1 justify-center">
            Annulla
          </button>
          <button
            type="submit"
            disabled={loading || channelIds.length === 0}
            className="ixam-btn flex-1 justify-center"
          >
            {loading ? 'Salvataggio…' : 'Salva regola'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({
  label,
  children,
  className = '',
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs font-medium text-ink mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function ChipGroup<T extends string>({
  label,
  options,
  value,
  labels,
  onToggle,
}: {
  label: string;
  options: readonly T[];
  value: T[];
  labels?: Record<T, string>;
  onToggle: (v: T) => void;
}) {
  return (
    <div>
      <div className="text-xs font-medium text-ink mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => {
          const active = value.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => onToggle(o)}
              className={`px-2 py-0.5 text-xs rounded-md border transition-colors ${
                active
                  ? 'bg-accent-soft text-accent-ink border-accent/30'
                  : 'bg-white text-ink-muted border-line hover:border-ink-muted'
              }`}
            >
              {labels?.[o] ?? o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
